import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  TextField,
  Typography,
} from "@mui/material";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import warning from "../../../assets/warning.svg";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../../components/BaseUrl";
import { toast } from "react-hot-toast";
import CloseIcon from '@mui/icons-material/Close';

const CancelRequestModal = ({
  open,
  loading = false,
  onConfirm,
  onClose,
  mode = "cancel", // "cancel" or "resubmit"
  token,
  requestType, // e.g. "LEAVE", "GENERIC_HR", "SCHEDULE_CHANGE"
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    additional_info: "",
    reason_text: "",
    end_date: "",
    subject: "",
    details: "",
    file_url: "",
  });

  const handleClose = () => {
    if (!loading) onClose?.();
  };

  // upload function (works for pdf or image)
  const handleFileUpload = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const isPdf = selectedFile.type === "application/pdf";
    const key = isPdf ? "pdf" : "image";
    const uploadUrl = isPdf
      ? `${BASE_URL}/upload/pdf`
      : `${BASE_URL}/upload/image`;

    const data = new FormData();
    data.append(key, selectedFile);

    try {
      setUploading(true);
      const res = await fetch(uploadUrl, { method: "POST", body: data });
      const json = await res.json();

      if (json?.data?.url) {
        // toast.success("File uploaded successfully");
        setFile(selectedFile);
        setFormData((prev) => ({ ...prev, file_url: json.data.url }));
      } else {
        toast.error("Upload failed");
      }
    } catch (err) {
      console.error("❌ Upload error:", err);
      toast.error("Error uploading file");
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (mode === "resubmit") {
      if (!formData.file_url) {
        toast.error("Please upload a file before resubmitting.");
        return;
      }

      const normalizedType =
        typeof requestType === "string"
          ? requestType.toLowerCase()
          : String(requestType || "").toLowerCase();

      let payload = {};

      if (normalizedType.includes("leave")) {
        payload = {
          additional_info: formData.additional_info,
          leave_reason_text: formData.reason_text,
          leave_end_date: formData.end_date,
          file_url: formData.file_url,
        };
      } else if (normalizedType.includes("schedule")) {
        payload = {
          additional_info: formData.additional_info,
          sched_reason_text: formData.reason_text,
          file_url: formData.file_url,
        };
      } else if (normalizedType.includes("generic_hr")) {
        payload = {
          additional_info: formData.additional_info,
          subject: formData.subject,
          details: formData.details,
          file_url: formData.file_url,
        };
      } else {
        payload = {
          additional_info: formData.additional_info,
          file_url: formData.file_url,
        };
      }

      onConfirm(payload); // send API call from parent
    } else {
      onConfirm();
    }
  };

  const titleMessage =
    mode === "resubmit"
      ? t("cancelRequestModal.titleResubmit")
      : t("cancelRequestModal.titleCancel");

  const confirmLabel =
    mode === "resubmit" ? t("cancelRequestModal.resubmitRequest") : t("cancelRequestModal.cancelRequest");
  const confirmColor = mode === "resubmit" ? "#006EC2" : undefined;

  // Normalize type once for conditional rendering
  const normalizedType =
    typeof requestType === "string"
      ? requestType.toLowerCase()
      : String(requestType || "").toLowerCase();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      keepMounted
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: {
          borderRadius: 3,
          textAlign: "center",
          p: 2,
          py: 1,
        },
      }}
      BackdropProps={{
        sx: { backdropFilter: "blur(1px)" },
      }}
    >
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
          <img src={warning} alt="warning" style={{ width: 50, height: 50 }} />
        </Box>

        <CustomText
          sx={{
            ...textStyles.h1,
            fontSize: 18,
            fontWeight: 500,
            lineHeight: "32px",
            color: "#2C384C",
            mb: 2,
          }}
        >
          {titleMessage}
        </CustomText>

        {mode === "resubmit" && (
          <Box sx={{ textAlign: "left" }}>
            <Typography
                  sx={{   fontSize: "14px",
  fontWeight: 500,
  fontFamily: "'Poppins', sans-serif",
color: "#6B778C"}}
            >
             {t("cancelRequestModal.additionalInfo")}
            </Typography>
            <textarea
              
              rows={2}
              value={formData.additional_info}
              onChange={(e) => handleChange("additional_info", e.target.value)}
                 className='project_input'
                  style={{ mb: 2 ,height:"80px",width:"100%"}}
            />

            {/* LEAVE request fields */}
            {normalizedType.includes("leave") && (
              <>
                <Typography
                       sx={{   fontSize: "14px",
  fontWeight: 500,
  fontFamily: "'Poppins', sans-serif",
color: "#6B778C"}}
                >
                 {t("cancelRequestModal.leaveReasonText")
}
                </Typography>
                <input
                
                  value={formData.reason_text}
                  onChange={(e) => handleChange("reason_text", e.target.value)}
                    className='project_input'
                  style={{ mb: 2 ,height:"40px",width:"100%"}}
                />

                <Typography
                    sx={{   fontSize: "14px",
  fontWeight: 500,
  fontFamily: "'Poppins', sans-serif",
color: "#6B778C"}}
                >
                  {t("cancelRequestModal.leaveEndDate")}
                </Typography>
                <input
                  fullWidth
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleChange("end_date", e.target.value)}
    className='project_input'
                  style={{ mb: 2 ,height:"40px",width:"100%"}}                />
              </>
            )}

            {/* SCHEDULE request fields */}
            {normalizedType.includes("schedule") && (
              <>
                <Typography
                    sx={{   fontSize: "14px",
  fontWeight: 500,
  fontFamily: "'Poppins', sans-serif",
color: "#6B778C"}}
                >
                {t("cancelRequestModal.scheduleReasonText")}
                </Typography>
                <input
                  fullWidth
                  value={formData.reason_text}
                  onChange={(e) => handleChange("reason_text", e.target.value)}
                     className='project_input'
                  style={{ mb: 2 ,height:"40px",width:"100%"}}
                />
              </>
            )}

            {/* GENERIC HR request fields */}
            {normalizedType.includes("generic_hr") && (
              <>
                <Typography
                      sx={{   fontSize: "14px",
  fontWeight: 500,
  fontFamily: "'Poppins', sans-serif",
color: "#6B778C"}}
                >
             {t("cancelRequestModal.subject")}
                </Typography>
                <input
                  fullWidth
                  value={formData.subject}
                  onChange={(e) => handleChange("subject", e.target.value)}
                     className='project_input'
                  style={{ mb: 2 ,height:"40px",width:"100%"}}
                />

                <Typography
                  sx={{   fontSize: "14px",
  fontWeight: 500,
  fontFamily: "'Poppins', sans-serif",
color: "#6B778C"}}
                >
                 {t("cancelRequestModal.details")}
                </Typography>
                <input
                  fullWidth
                  multiline
                  rows={2}
                  
                  value={formData.details}
                  onChange={(e) => handleChange("details", e.target.value)}
                  className='project_input'
                  style={{ mb: 2 ,height:"40px",width:"100%"}}
                />
              </>
            )}

            {/* File Upload */}
         <Box
  sx={{
    border: "2px dashed #ccc",
    borderRadius: 2,
    p: 2,
    mt: 2,
    textAlign: "center",
    height: "120px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    backgroundColor: file ? "#F9FAFB" : "transparent",
  }}
>
  <input
    type="file"
    accept="image/*,application/pdf"
    onChange={handleFileUpload}
    id="upload"
    style={{ display: "none" }}
  />

  {/* Show Upload Prompt if no file */}
  {!file ? (
    <label htmlFor="upload" style={{ cursor: "pointer" }}>
      {uploading ? (
        <CircularProgress size={20} />
      ) : (
        <Typography
          sx={{
            color: "#006EC2",
            fontWeight: 500,
            fontSize: "14px",
            fontFamily: "Poppins, sans-serif",
          }}
        >
          {t("cancelRequestModal.uploadPrompt")}
        </Typography>
      )}
    </label>
  ) : (
    // Show Uploaded File with Delete Icon
    <Box
      sx={{
        width: "100%",
        textAlign: "center",
        wordBreak: "break-all",
        position: "relative",
      }}
    >
      <Typography
        sx={{
          color: "#172B4D",
          fontWeight: 500,
          fontSize: "14px",
          fontFamily: "Poppins, sans-serif",
        }}
      >
        {file.name}
      </Typography>

      {/* Delete (×) Icon */}
      <Box
        component="span"
        onClick={() => {
          setFile(null);
          setFormData((prev) => ({ ...prev, file_url: "" }));
        }}
        sx={{
          position: "absolute",
          top:-20,
          right: -8,
          width: 22,
          height: 22,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          backgroundColor: "#FF4C4C",
          color: "white",
          fontSize: 14,
        
          cursor: "pointer",
         
          "&:hover": { backgroundColor: "#E03E3E" },
        }}
      >
        ×
      </Box>
    </Box>
  )}
</Box>

          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
          gap: 1.5,
          pb: 2.5,
          pt: 0,
        }}
      >
        <CustomButton
          onClick={handleClose}
          disabled={loading}
          size="medium"
          label={t("cancelRequestModal.cancel")}
          sx={{
            color: "#172B4D",
            backgroundColor: "white",
            "&:hover": { backgroundColor: "white", color: "#172B4D" },
          }}
        />

        <CustomButton
          onClick={handleSubmit}
          disabled={loading || uploading}
          size="medium"
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {(loading || uploading) && (
                <CircularProgress size={18} thickness={4} sx={{ color: "#fff" }} />
              )}
              {confirmLabel}
            </Box>
          }
          sx={{
            backgroundColor: confirmColor,
            "&:hover": confirmColor ? { backgroundColor: confirmColor } : {},
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default CancelRequestModal;



